import { useQuery } from '@tanstack/react-query';
import { useApi, type CourseReport, type QuizReport } from '@/lib/api';
import { Badge, Card, ErrorText } from '@/components/ui';
import { SkillBars } from '@/components/quiz/skill-bars';
import { useOrgContext } from './layout';

/** Course and quiz results for this organization and its child organizations. */
export function OrgReportsPage() {
  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Courses</h2>
        <CourseReports />
      </section>
      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Quizzes</h2>
        <QuizReports />
      </section>
    </div>
  );
}

/**
 * Course progress from members of this organization and its child
 * organizations: its own courses and Nirvik's public ones. An enrollment
 * counts once the learner was a member when they enrolled or finished.
 */
function CourseReports() {
  const { org } = useOrgContext();
  const api = useApi();
  const report = useQuery({
    queryKey: ['courseReport', org.id],
    queryFn: () => api.courseReport(org.id),
  });
  if (report.isError) return <ErrorText error={report.error} />;
  if (report.data?.courses.length === 0) {
    return (
      <Card>
        <p className="text-sm text-muted-foreground">
          No course activity yet. It appears here once members start courses.
        </p>
      </Card>
    );
  }
  return <CourseReportCards courses={report.data?.courses ?? []} />;
}

/** One card per course: enrollments, progress, certificates and final assessment results. */
export function CourseReportCards({ courses }: { courses: CourseReport[] }) {
  return (
    <>
      {courses.map((c) => (
        <Card key={c.courseId} title={c.title} action={<Badge>{c.from}</Badge>}>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            {[
              ['Learners', c.learners.toLocaleString()],
              ['Completed', c.completed.toLocaleString()],
              ['In progress', c.inProgress.toLocaleString()],
              ['Average progress', `${c.averagePercent}%`],
              ['Certificates', c.certificates.toLocaleString()],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-sm text-muted-foreground">{label}</dt>
                <dd className="text-2xl font-semibold tabular-nums">{value}</dd>
              </div>
            ))}
          </dl>
          {c.assessment && (
            <>
              <h3 className="mb-3 mt-5 text-sm font-medium">Final assessment</h3>
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-5">
                {[
                  ['Attempts', c.assessment.attempts.toLocaleString()],
                  ['Average score', `${c.assessment.averagePercent}%`],
                  ['Pass rate', `${c.assessment.passRate}%`],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-sm text-muted-foreground">{label}</dt>
                    <dd className="text-2xl font-semibold tabular-nums">{value}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}
        </Card>
      ))}
    </>
  );
}

/**
 * Quiz results from members of this organization and its child
 * organizations: its own quizzes and Nirvik's public ones, counted when the
 * member finished. Results from before someone joined aren't included.
 */
function QuizReports() {
  const { org } = useOrgContext();
  const api = useApi();
  const report = useQuery({
    queryKey: ['quizReport', org.id],
    queryFn: () => api.quizReport(org.id),
  });

  if (report.isError) return <ErrorText error={report.error} />;
  if (report.data?.quizzes.length === 0) {
    return (
      <Card>
        <p className="text-sm text-muted-foreground">
          No results yet. They appear here once members finish your organization's quizzes.
        </p>
      </Card>
    );
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Results from your members on your quizzes and Nirvik's public quizzes, including child
        organizations. Counted from when each member finished; results from before someone joined
        aren't included.
      </p>
      <QuizReportCards quizzes={report.data?.quizzes ?? []} />
    </div>
  );
}

/** One card per quiz: learners, attempts, scores, certificates and skills. */
export function QuizReportCards({ quizzes }: { quizzes: QuizReport[] }) {
  return (
    <>
      {quizzes.map((q) => (
        <Card key={q.quizId} title={q.title} action={<Badge>{q.from}</Badge>}>
          <dl className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-5">
            {[
              ['Learners', q.learners.toLocaleString()],
              ['Attempts', q.attempts.toLocaleString()],
              ['Average score', `${q.averagePercent}%`],
              ['Pass rate', `${q.passRate}%`],
              ['Certificates', q.certificates.toLocaleString()],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-sm text-muted-foreground">{label}</dt>
                <dd className="text-2xl font-semibold tabular-nums">{value}</dd>
              </div>
            ))}
          </dl>
          <h3 className="mb-3 text-sm font-medium">Average by skill</h3>
          <SkillBars skills={q.skills} />
        </Card>
      ))}
    </>
  );
}
